import { Component, inject, signal } from '@angular/core';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../../core/services/auth.service';
import { ThemeService } from '../../../core/services/theme.service';
import { CatanPinService } from '../../../core/services/catan-pin.service';
import { CatanPinDialogComponent } from './catan-pin-dialog.component';

/**
 * Navigacija za catan.my-famil.io.
 *
 * Zamjenjuje obiteljski sidebar i bottom nav — Catan je zaseban proizvod
 * i vecina posjetitelja nije prijavljena.
 */
@Component({
  selector: 'app-catan-shell',
  standalone: true,
  imports: [RouterLink, ButtonModule, CatanPinDialogComponent],
  template: `
    <header class="catan-header">
      <a routerLink="/catan" class="catan-brand">
        <span class="catan-brand-mark">🎲</span>
        <span class="catan-brand-text">Catan</span>
      </a>

      <nav class="catan-nav">
        <a routerLink="/catan" class="catan-nav-item" [class.active]="url() === '/catan'"> Scoreboard </a>
        <a routerLink="/catan/games" class="catan-nav-item" [class.active]="url().startsWith('/catan/games')"> Igre </a>
        <a routerLink="/catan/players" class="catan-nav-item" [class.active]="url().startsWith('/catan/players')">
          Igrači
        </a>
      </nav>

      <div class="catan-actions">
        <p-button
          [icon]="theme.darkMode() ? 'pi pi-sun' : 'pi pi-moon'"
          [text]="true"
          severity="secondary"
          size="small"
          (onClick)="theme.toggle()"
          [ariaLabel]="theme.darkMode() ? 'Svijetla tema' : 'Tamna tema'"
        />

        @if (pin.unlocked()) {
          <a [routerLink]="['/catan/players', pin.playerId()]" class="catan-me">
            <i class="pi pi-user"></i>
            <span class="catan-me-name">{{ pin.displayName() }}</span>
          </a>
          <p-button
            icon="pi pi-lock-open"
            [text]="true"
            severity="success"
            size="small"
            (onClick)="pin.lock()"
            ariaLabel="Zaključaj unos"
          />
        } @else {
          <p-button icon="pi pi-lock" label="Moj PIN" [text]="true" size="small" (onClick)="pinDialog.set(true)" />
        }

        @if (auth.isLoggedIn()) {
          <p-button
            icon="pi pi-sign-out"
            [text]="true"
            severity="secondary"
            size="small"
            (onClick)="auth.logout()"
            ariaLabel="Odjava"
          />
        } @else {
          <p-button icon="pi pi-sign-in" [text]="true" severity="secondary" size="small" (onClick)="goToLogin()" />
        }
      </div>
    </header>

    <app-catan-pin-dialog [(visible)]="pinDialog" />
  `,
  styles: [
    `
      .catan-header {
        position: sticky;
        top: 0;
        z-index: 100;
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.6rem 1rem;
        background: var(--p-content-background);
        border-bottom: 1px solid var(--p-content-border-color);
      }

      .catan-brand {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: 700;
        font-size: 1.1rem;
        color: var(--p-text-color);
        text-decoration: none;
      }

      .catan-brand-mark {
        font-size: 1.4rem;
        line-height: 1;
      }

      .catan-nav {
        display: flex;
        gap: 0.25rem;
        margin-left: auto;
      }

      .catan-nav-item {
        padding: 0.4rem 0.75rem;
        border-radius: 6px;
        color: var(--p-text-muted-color);
        text-decoration: none;
        font-size: 0.9rem;
        font-weight: 500;
        white-space: nowrap;
      }

      .catan-nav-item:hover {
        background: var(--p-content-hover-background);
        color: var(--p-text-color);
      }

      .catan-nav-item.active {
        background: var(--p-primary-color);
        color: var(--p-primary-contrast-color);
      }

      .catan-actions {
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .catan-me {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.3rem 0.6rem;
        border-radius: 999px;
        background: var(--p-content-hover-background);
        color: var(--p-text-color);
        text-decoration: none;
        font-size: 0.85rem;
        font-weight: 600;
        max-width: 9rem;
      }

      .catan-me-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      @media (max-width: 640px) {
        .catan-header {
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .catan-nav {
          order: 3;
          width: 100%;
          margin-left: 0;
          justify-content: space-between;
        }

        .catan-nav-item {
          flex: 1;
          text-align: center;
        }

        .catan-actions {
          margin-left: auto;
        }

        .catan-brand-text {
          display: none;
        }
      }
    `,
  ],
})
export class CatanShellComponent {
  readonly auth = inject(AuthService);
  readonly theme = inject(ThemeService);
  readonly pin = inject(CatanPinService);

  private router = inject(Router);

  readonly url = signal(this.router.url);
  readonly pinDialog = signal(false);

  constructor() {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      this.url.set(e.urlAfterRedirects);
    });
  }

  goToLogin(): void {
    void this.router.navigate(['/login']);
  }
}
